import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ViewPlayers from "./ViewPlayers";
import PayNow from "./PayNow";
import PayLater from "./PayLater";
import apiService, { formatRupees, handleError } from "../../Common/ApiCommon/ApiService";
import toast from "react-hot-toast";
import icons from "../../../assets/images/constantIcons";
import { useNavigate } from "react-router-dom";
import EditBookingForm from "./EditBookingForm";
import DeleteBooking from "./DeleteBooking";
import PlayTimer from "./LiveTimer";
import { UserContext } from "../../Layout/UserContext";
import EditPayableForm from "./EditPayableForm";


const useAddPlayer = (showPreviousBooking, redirectedBookingId) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [viewOpen, setViewOpen] = useState(null);
  const [payNow, setPayNow] = useState(null);
  const [payLater, setPayLater] = useState(null);
  const [PlayersData, setPlayersData] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [editBooking, setEditBooking] = useState(null);
  const [editPayableBooking, setPayableEditBooking] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [deletePopupIndex, setDeletePopupIndex] = useState(null);
  const navigate = useNavigate();
  const [hasStarted, setHasStarted] = useState(false);
  const { triggerNotificationRefresh } = useContext(UserContext);

  const fetchPlayers = useCallback(
    async (type = "") => {
      setLoading(true);
      try {
        let url = "/bookings";
        const params = new URLSearchParams();

        if (type === "previous") {
          params.append("filter", "previous");
        }
         if (type === "yesterday") {
          params.append("filter", "yesterday");
        }
        if (type === "custom" && fromDate && toDate) {
          params.append("from_date", fromDate);
          params.append("to_date", toDate);
          params.append("filter", "custom");
        }
        if (searchQuery) {
          params.append("search", searchQuery);
        }
        const response = await apiService.get(`${url}?${params.toString()}`);
        setPlayersData(response.data);
        triggerNotificationRefresh();
      } catch (errors) {
        handleError(errors);
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, fromDate, toDate, filterType]
  );

  useEffect(() => {
    if (redirectedBookingId) {
      setViewOpen(redirectedBookingId);
    }
    if (showPreviousBooking === "previous" && searchQuery === "") {
      setFilterType("previous");
      fetchPlayers("previous");
    } else {
      fetchPlayers();
    }
  }, [redirectedBookingId, showPreviousBooking,searchQuery]);

  const handleView = (id) => {
    setViewOpen(id);
  };
  const handleViewPayNow = (id) => {
    setPayNow(id);
  };

  const handleDeleteBoking = (id) => {
    setDeletePopupIndex(id);
  };
  const handleViewPayLater = (id) => {
    setPayLater(id);
  };
  const handleViewEdit = (id) => {
    setEditBooking(id);
  };

  const handlePayableEdit = (id) => {
    setPayableEditBooking(id);
  };
  // filter type
  const filter = filterType === "previous" || filterType === "yesterday";
  const filterCustom = filterType == "custom";

  const columns = useMemo(
    () => [
      {
        Header: "No",
        accessor: "serial_number",
        headerAlign: "text-center",
        cellAlign: "text-center",
      },
      {
        Header: "TABLE TYPE",
        accessor: "tableType",
        Cell: ({ value }) => {
          return (
            <div
              className={`bg-green700 text-white rounded-3xl py-[6px] px-4 md:px-2 text-center text-wrap w-auto md:w-[100px]`}
            >
              {value}
            </div>
          );
        },
      },
      {
        Header: "PLAYERS",
        accessor: "players",
        headerAlign: "text-center",
        cellAlign: "text-center",
        Cell: ({ value }) => {
          const playerNames = value.split(",").map((name) => name.trim());
          return (
            <div className="w-[170px] group ">
              <ul className="flex flex-wrap justify-start lg:justify-center items-center">
                {playerNames.slice(0, 2).map((name, index) => (
                  <li key={index} className="break-words whitespace-normal">
                    {name}
                    {index !== playerNames.length - 1 && " / "}
                  </li>
                ))}
                {playerNames.length > 2 && <span className="font-bold"> ...</span>}
              </ul>

              {/* Full list visible on hover */}
              {playerNames.length > 2 && (
                <div className="absolute bg-white shadow-lg p-2 rounded-md border border-gray-300 z-10 hidden group-hover:block max-w-[500px]">
                  <span>{playerNames.join(" / ")}</span>
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: ({ data }) =>
          (data.some((row) => row.payable_players?.length) &&
            (filter || filterCustom))
            ? "Payable"
            : null,
        accessor: "payable_players",
        disableSortBy: !filter,
        Cell: ({ row }) => {
          const payables = row.original.payable_players || [];
          const displayedPayables = payables.slice(0, 2);

          return (
            <div className="w-auto group ">
              <div className="flex flex-wrap justify-start lg:justify-center items-center">
                {displayedPayables.map(({ name, id }, index) => (
                  <span
                    key={id}
                    className="cursor-pointer hover:text-blue hover:underline"
                    onClick={() => {
                      navigate(`/mainDashboard/customer`, { state: id });
                    }}
                  >
                    {name}
                    {index !== displayedPayables.length - 1 && " / "}

                  </span>
                ))}
                {payables.length > 2 && <span className="font-bold"> ...</span>}
              </div>
              {payables.length > 2 && (
                <div className="absolute bg-white shadow-lg p-2 rounded-md border border-gray-300 z-10 hidden group-hover:block max-w-[500px]">
                  {payables.map(({ name, id }, index) => (
                    <span
                      key={id}
                      className="cursor-pointer text-blue-600 hover:underline"
                      onClick={() => {
                        navigate(`/mainDashboard/customer`, { state: id });
                      }}
                    >
                      {name}
                      {index < payables.length - 1 && " / "}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        }
      },
      ...(filterType == "custom"
        ? [
          {
            Header: "Date",
            accessor: "start_date",
          },
        ]
        : []),
      {
        Header: "START TIME",
        accessor: "start_time",
      },
      {
        Header: ({ data }) =>
          (data.some((row) => row.end_time) && (filter || filterCustom)) ? "End TIME" : null,
        accessor: "end_time",
        disableSortBy: !filter,
      },
      ...(filterType !== "previous"
        ? [
          {
            Header: "Play TIME",
            accessor: "play_time",
            headerAlign: "text-left",
            cellAlign: "text-left",
            disableSortBy: true,
            Cell: ({ row }) => {
              return (
                <PlayTimer
                  startTime={row.original.start_time}
                  startDate={row.original.start_date}
                />
              )
            },
          },
        ]
        : []),
      {
        Header: ({ data }) => {
          const showHeader = data.some((row) => row.duration);
          return showHeader ? "DURATION" : null;
        },
        accessor: "duration",
        disableSortBy: true,
        Cell: ({ value, row }) => {
          if (!value) return null;
          return <div className="text-nowrap">{value}</div>;
        },
      },
      {
        Header: ({ data }) =>
          (data.some((row) => row.today_amount && row.today_amount !== "₹ 0") && (filter || filterCustom)) ? "AMOUNT" : null,
        accessor: "today_amount",
        disableSortBy: !filter,
      },
      {
        Header: "Action",
        accessor: "action",
        disableSortBy: true,
        Cell: function ActionCell({ row }) {
          const hasEnded = !!row.original.end_time;
          const { start_date, start_time } = row.original;
          const [hasStarted, setHasStarted] = useState(false);

          // const isPrevious = filterType === "previous";
          // const isCustom = filterType === "custom";

          // const isPaid = row.original.is_paid;

          useEffect(() => {
            if (!start_date || !start_time) return;

            const convertTo24HourFormat = (time) => {
              const [timePart, modifier] = time.split(" ");
              let [hours, minutes] = timePart.split(":").map(Number);
              if (modifier === "PM" && hours !== 12) hours += 12;
              if (modifier === "AM" && hours === 12) hours = 0;
              return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
            };

            const formattedTime = convertTo24HourFormat(start_time);
            const bookingStartTime = new Date(`${start_date}T${formattedTime}`);

            const updateHasStarted = () => {
              const now = new Date();
              if (now >= bookingStartTime) {
                setHasStarted(true);
              } else {
                setHasStarted(false);
              }
            };

            updateHasStarted(); // Initial check
            const interval = setInterval(updateHasStarted, 1000); // Update every second

            return () => clearInterval(interval);
          }, [start_date, start_time]);

          const handlePaymentClick = () => {
            if (!hasStarted) {
              toast.error("Booking time has not started yet!", {
                duration: 1000,
              });
            }
          };

          const tableCharge = parseFloat(row.original.table_charge?.replace(/[^\d.-]/g, "")) || 0;
          const today_amount = parseFloat(row.original.today_amount?.replace(/[^\d.-]/g, "")) || 0;
          return (
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="grid grid-cols-3 sm:flex sm:gap-3">
                <div
                  onClick={() => handleView(row.original.id)}
                  className="flex items-center justify-center focus:outline-none w-[40px] h-[40px] rounded-full p-2 bg-green50 text-green cursor-pointer"
                >
                  <icons.IoEyeOutline className="text-xl font-bold" />
                </div>
                {/* view detail form */}
                {viewOpen === row.original.id && (
                  <ViewPlayers
                    isOpen={viewOpen === row.original.id}
                    onClose={() => handleView(null)}
                    booking_id={row.original.id}
                  />
                )}

                {/* edit form */}
                {!hasEnded && (
                  <button
                    onClick={() => handleViewEdit(row.original.id)}
                    className="p-2 flex items-center justify-center focus:outline-none w-[40px] h-[40px] rounded-full bg-green bg-opacity-[10%]" >
                    <icons.FaRegEdit className="text-xl font-bold text-green" />
                  </button>
                )}

                {editBooking === row.original.id && (
                  <EditBookingForm
                    isOpen={editBooking === row.original.id}
                    onClose={() => handleViewEdit(null)}
                    booking_id={row.original.id}
                    fetchPlayers={fetchPlayers}
                  />
                )}

                {editPayableBooking === row.original.id && (
                  <EditPayableForm
                    isOpen={editPayableBooking === row.original.id}
                    onClose={() => handlePayableEdit(null)}
                    booking_id={row.original.id}
                    startTime={row.original.start_time}
                    tableCharge={tableCharge}
                    duration ={row.original.duration}
                    today_amount={today_amount}
                    fetchPlayers={fetchPlayers}
                  />
                )}

                {/* delete bookings */}
                {!hasEnded && (
                  <button
                    onClick={() => handleDeleteBoking(row.original.id)}
                    className="p-2 flex items-center justify-center focus:outline-none w-[40px] h-[40px] rounded-full bg-red bg-opacity-[10%]"
                  >
                    <icons.AiOutlineDelete className="text-xl  text-red font-bold" />
                  </button>
                )}

                {deletePopupIndex === row.original.id && (
                  <DeleteBooking
                    isOpen={deletePopupIndex === row.original.id}
                    onClose={() => handleDeleteBoking(null)}
                    booking_id={row.original.id}
                    fetchPlayers={fetchPlayers}
                  />
                )}


              </div>
              <div className="grid grid-cols-2 gap-3 sm:flex">
                {/* pay now ,pay later button */}
                {!hasEnded && (
                  <>
                    <button
                      onClick={() =>
                        hasStarted
                          ? handleViewPayNow(row.original.id)
                          : handlePaymentClick()
                      }
                      className={`text-white text-center rounded-[32px] w-auto sm:w-[100px] px-4 py-2 sm:py-1  
                ${hasStarted
                          ? "bg-checkoutblue cursor-pointer"
                          : "bg-gray-300 cursor-not-allowed"
                        }
              `}
                    >
                      Pay Now
                    </button>

                    {payNow === row.original.id && (
                      <PayNow
                        isOpen={payNow === row.original.id}
                        onClose={() => handleViewPayNow()}
                        booking_id={row.original.id}
                        startTime={row.original.start_time}
                        tableCharge={tableCharge}
                        tableName={row.original.tableType}
                        fetchPlayers={fetchPlayers}
                        loading={loading}
                      />
                    )}

                    <button
                      onClick={() =>
                        hasStarted
                          ? handleViewPayLater(row.original.id)
                          : handlePaymentClick()
                      }
                      className={`text-white text-center rounded-[32px] w-auto sm:w-[100px] px-4 py-2 sm:py-1  
                ${hasStarted
                          ? "bg-orange cursor-pointer"
                          : "bg-gray-300 cursor-not-allowed"
                        }
              `}
                    >
                      Pay Later
                    </button>

                    {payLater === row.original.id && (
                      <PayLater
                        isOpen={payLater === row.original.id}
                        onClose={() => handleViewPayLater(null)}
                        booking_id={row.original.id}
                        startTime={row.original.start_time}
                        tableCharge={tableCharge}
                        tableName={row.original.tableType}
                        fetchPlayers={fetchPlayers}
                         loading={loading}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          );
        },
      },
    ],
    [viewOpen, payNow, payLater, editBooking, deletePopupIndex, filterType, fromDate, toDate, editPayableBooking]
  );

  const data = useMemo(() => {
    if (!Array.isArray(PlayersData)) return [];
    return PlayersData?.map((item) => ({
      serial_number: item.serial_number,
      id: item.id,
      tableType: item.table_type,
      players: item.players?.length > 0
        ? item.players.map((player) => player.name).join(", ")
        : "No players",
      payable_id: item.payable_id,
      payable_players: item.payable_players,
      start_time: item.start_time,
      end_date: item.end_date,
      end_time: item.end_time,
      duration: item.duration,
      today_amount: Math.trunc(item.today_amount) ? formatRupees(item.today_amount) : null,
      start_date: item.start_date.split("T")[0],
      table_charge: item.table_charge,
      status: item.status,
      booking_duration: item.booking_duration,
      total_amount: item.total_amount,
      is_paid:item.is_paid,
      due_amount:item.due_amount,
      amount_to_be_pay:item.amount_to_be_pay,
      discount:item.discount,
    }));
  }, [PlayersData]);

  // Paginate the data
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to page 1 when rows per page change
  };

  return {
    columns,
    currentRows,
    totalRows: data.length,
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    PlayersData,
    fetchPlayers,
    filterType,
    setFilterType,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    searchQuery,
    setSearchQuery,
    loading,
    viewOpen,
    setViewOpen,
    data
  };
};

export default useAddPlayer;
